import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer, Cell,
} from 'recharts';
import type { MarketPriceRow } from '../../hooks/useMarketAnalysis';

/**
 * 価格チャートの下に並べる補助チャート (出来高・RSI)。
 * 二軸チャートを避け、同じ横軸で縦に積む。
 */

const fmtDate = (d: string) => (d ? `${d.slice(2, 4)}/${d.slice(5, 7)}/${d.slice(8, 10)}` : '');
const fmtVolume = (v: number) => (v >= 1e8 ? `${(v / 1e8).toFixed(1)}億` : v >= 1e4 ? `${(v / 1e4).toFixed(0)}万` : v.toLocaleString());

const wrapSx = { p: 2, backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' };
const titleSx = { fontSize: '13px', fontWeight: 700, color: '#475569', mb: 0.5 };
const tipSx = { backgroundColor: '#334155', color: '#fff', borderRadius: '8px', px: 1.5, py: 0.75, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.2)' };

type Props = { rows: MarketPriceRow[] };

export function MarketVolumeChart({ rows }: Props) {
  const tickInterval = Math.max(0, Math.floor(rows.length / 8) - 1);
  const VolumeTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || payload.length === 0) return null;
    const r: MarketPriceRow = payload[0].payload;
    return (
      <Box sx={tipSx}>
        <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>{label}</Typography>
        <Typography sx={{ fontSize: '11px' }}>出来高 {r.volume != null ? r.volume.toLocaleString() : '-'}</Typography>
      </Box>
    );
  };
  return (
    <Box sx={wrapSx}>
      <Typography sx={titleSx}>出来高</Typography>
      <ResponsiveContainer width="100%" height={120}>
        <ComposedChart data={rows} margin={{ top: 4, right: 20, left: 10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
          <XAxis dataKey="trade_date" tickFormatter={fmtDate} interval={tickInterval} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
          <YAxis tickFormatter={fmtVolume} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} width={72} />
          <Tooltip content={<VolumeTooltip />} cursor={{ fill: 'rgba(59, 130, 246, 0.05)' }} />
          <Bar dataKey="volume" name="出来高" isAnimationActive={false} maxBarSize={8}>
            {rows.map((r, i) => (
              // 前日比プラスは青、マイナスは灰で塗り分け (色は補助。値は tooltip で読む)
              <Cell key={i} fill={(r.change ?? 0) >= 0 ? '#93c5fd' : '#cbd5e1'} />
            ))}
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}

export function MarketRsiChart({ rows }: Props) {
  const tickInterval = Math.max(0, Math.floor(rows.length / 8) - 1);
  const RsiTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || payload.length === 0) return null;
    const r: MarketPriceRow = payload[0].payload;
    return (
      <Box sx={tipSx}>
        <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>{label}</Typography>
        <Typography sx={{ fontSize: '11px' }}>RSI14 {r.rsi_14 != null ? r.rsi_14.toFixed(1) : '-'}</Typography>
      </Box>
    );
  };
  return (
    <Box sx={wrapSx}>
      <Typography sx={titleSx}>RSI (14 日) <span style={{ fontWeight: 500, color: '#94a3b8' }}>70 以上は買われ過ぎ、30 以下は売られ過ぎの目安</span></Typography>
      <ResponsiveContainer width="100%" height={140}>
        <ComposedChart data={rows} margin={{ top: 4, right: 20, left: 10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
          <XAxis dataKey="trade_date" tickFormatter={fmtDate} interval={tickInterval} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
          <YAxis domain={[0, 100]} ticks={[0, 30, 50, 70, 100]} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} width={72} />
          <Tooltip content={<RsiTooltip />} cursor={{ stroke: '#94a3b8', strokeDasharray: '3 3' }} />
          <ReferenceLine y={70} stroke="#dc2626" strokeDasharray="4 2" strokeOpacity={0.6} />
          <ReferenceLine y={30} stroke="#059669" strokeDasharray="4 2" strokeOpacity={0.6} />
          <Line type="monotone" dataKey="rsi_14" name="RSI14" stroke="#7c3aed" strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}
